import { Button } from "@/components/ui/button";
import { Heart } from "lucide-react";
import { useState } from "react";

interface RaceWeekendLikeButtonProps {
  userId: string;
  iracingSessionId: string;
  isLiked: boolean;
  likesCount: number;
  totalSessions: number;
  className?: string;
}

export default function RaceWeekendLikeButton({
  userId,
  iracingSessionId,
  isLiked: initialIsLiked,
  likesCount: initialLikesCount,
  totalSessions,
  className = "",
}: RaceWeekendLikeButtonProps) {
  const [isLiked, setIsLiked] = useState(initialIsLiked);
  const [likesCount, setLikesCount] = useState(initialLikesCount);
  const [isLoading, setIsLoading] = useState(false);

  const handleLike = async () => {
    if (isLoading) return;

    setIsLoading(true);

    try {
      const url = `/race_weekend/${userId}/${iracingSessionId}/like`;
      const response = await fetch(url, {
        method: isLiked ? "DELETE" : "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token":
            document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
              ?.content || "",
        },
      });

      const data = await response.json();

      if (data.success) {
        setIsLiked(data.liked);
        setLikesCount(data.likes_count);

        // Show feedback about how many sessions were liked/unliked
        const action = data.liked ? "liked" : "unliked";
        const sessionsAffected =
          data.sessions_liked || data.sessions_unliked || 0;
        if (sessionsAffected > 0) {
          console.log(
            `${action} ${sessionsAffected}/${totalSessions} sessions in this race weekend`
          );
        }
      } else {
        console.error("Failed to update race weekend like:", data.error);
      }
    } catch (error) {
      console.error("Error updating race weekend like:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Button
      onClick={handleLike}
      disabled={isLoading}
      variant="ghost"
      size="sm"
      className={`flex items-center gap-1.5 transition-all duration-300 hover:bg-transparent hover:text-current ${className}`}
      title={`${
        isLiked ? "Unlike" : "Like"
      } all ${totalSessions} sessions in this race weekend`}
    >
      <Heart
        className={`h-5 w-5 transition-all ${
          isLiked
            ? "fill-red-500 text-red-500"
            : "text-gray-400 hover:text-red-500"
        }`}
      />
      {likesCount > 0 && (
        <span className="text-sm font-medium">{likesCount}</span>
      )}
    </Button>
  );
}
